import React from "react";
import { Link } from "react-router-dom";
import {
  Calendar,
  ArrowRight,
  Sparkles,
  Phone,
  Mail,
} from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";
import { STAGES, etatStage, stagesParAnnee } from "../lib/stages";

export default function Stages() {
  useSEO({
    title: "Stages de danse pendant les vacances — 1000 ARTS Cugy VD",
    description:
      "Stages de danse à 1000 ARTS Cugy VD pendant les vacances scolaires (automne, hiver, printemps, été). Hip-hop, Tennis, comédie musicale pour enfants dès 5 ans. Découvre les stages ouverts.",
    canonical: "/stages",
  });

  return (
    <div
      data-testid="page-stages"
      className="pt-32 pb-24 overflow-hidden relative mesh-bg"
    >
      <PageDecor />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 z-10">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow mb-3 justify-center inline-flex items-center gap-2">
            <Sparkles size={14} /> Stages de vacances
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
            Stages <span style={{ color: "var(--primary)" }}>1000 ARTS</span>
          </h1>
          <p className="mt-6 text-lg text-[var(--muted)] leading-relaxed">
            Pendant les vacances scolaires, on transforme les journées en aventures — danse, chant, comédie musicale et pluri‑disciplinaire. Clique sur une bulle pour découvrir la page complète du stage : programme, tarifs, parrainage et inscription en un clic.
          </p>
          <p className="mt-4 text-base">
            <Link to="/dates-importantes" data-testid="stages-lien-dates" className="underline" style={{ color: "var(--primary)" }}>
              Voir toutes les dates importantes de la saison
            </Link>
          </p>
        </div>

        {/* Les stages par année (Jen, 30.10.2026) : d'abord ce qui vient, puis les stages
            passés, pour voir tout de suite ce qui est ouvert et ce qui est terminé. */}
        <div className="mt-16 max-w-[1000px] mx-auto space-y-14" data-testid="stages-grid">
          {stagesParAnnee(STAGES).map((g) => (
            <section key={g.annee} data-testid={`stages-annee-${g.annee}`}>
              <div className="flex items-center gap-4 mb-6">
                <h2 className="font-display text-4xl md:text-5xl tracking-tight" style={{ color: g.a_venir ? "var(--text)" : "var(--muted)" }}>{g.annee}</h2>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest"
                  style={g.a_venir ? { background: "var(--primary-tint)", color: "var(--primary)" } : { background: "#E5E7EB", color: "#4B5563" }}>
                  {g.a_venir ? "À venir" : g.stages.length > 1 ? "Stages terminés" : "Stage terminé"}
                </span>
                <span className="flex-1 border-t" style={{ borderColor: "var(--border-soft)" }} aria-hidden="true" />
              </div>
              <div className="grid gap-8 sm:grid-cols-2">
                {g.stages.map((s) => <SeasonBubble key={s.id} stage={s} />)}
              </div>
            </section>
          ))}
        </div>

        <ProchainStage />

        {/* INFOS */}
        <section
          className="relative max-w-[1000px] mx-auto mt-20"
          data-testid="stage-infos"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card-flat">
              <div className="eyebrow mb-3">Infos & réservation</div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <span
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: "var(--primary-tint)" }}
                  >
                    <Phone size={18} style={{ color: "var(--primary)" }} />
                  </span>
                  <a href="tel:+41799504031" className="hover:underline">
                    079 950 40 31
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <span
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: "var(--primary-tint)" }}
                  >
                    <Mail size={18} style={{ color: "var(--primary)" }} />
                  </span>
                  <a
                    href="mailto:info1000arts@gmail.com"
                    className="hover:underline break-all"
                  >
                    info1000arts@gmail.com
                  </a>
                </li>
              </ul>
            </div>
            <div
              className="card-flat"
              style={{ background: "var(--primary-tint)", borderColor: "transparent" }}
            >
              <div className="eyebrow mb-3">Bon à savoir</div>
              <ul className="space-y-2 text-sm text-[var(--text)]">
                <li>• Membres 1000 ARTS : tarif préférentiel sur tous les stages</li>
                <li>• Parrainage possible : jusqu'à 3 invités extérieurs par membre (–50.– par invité)</li>
                <li>• Chaque stage se termine par un mini-spectacle</li>
              </ul>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 mt-5 font-semibold text-sm"
                style={{ color: "var(--primary)" }}
                data-testid="stage-contact-link"
              >
                Nous contacter <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/* En bas de la liste : s'inscrire si un stage est ouvert ; sinon dire franchement qu'aucun ne
   l'est encore, et quel est le prochain (plutôt qu'un bouton qui ne mène à rien). */
function ProchainStage() {
  const ouverts = STAGES.filter((s) => etatStage(s) === "ouvert");
  const prochain = STAGES.find((s) => etatStage(s) === "date_a_venir");
  if (ouverts.length) {
    return (
      <div className="mt-12 flex justify-center" data-testid="stages-inscription-cta">
        <Link
          to="/inscription?type=stage"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold shadow-lg hover:scale-105 transition-transform"
          style={{ background: "var(--primary)", color: "#fff" }}
        >
          <Sparkles size={18} /> S'inscrire à un stage
          <ArrowRight size={16} />
        </Link>
      </div>
    );
  }
  return (
    <div className="mt-12 max-w-[1000px] mx-auto card-flat text-center" data-testid="stages-aucun-ouvert">
      <p className="font-semibold">Aucun stage n'est ouvert aux inscriptions pour le moment.</p>
      {prochain && (
        <p className="text-[var(--muted)] mt-2">
          Le prochain : <Link to={`/stages/${prochain.id}`} className="underline" style={{ color: "var(--primary)" }}>{prochain.title}</Link>
          {" "}({prochain.date_label.replace(/^Date à venir — /, "")}). Dates, programme et tarifs seront annoncés ici dès qu'ils sont fixés.
        </p>
      )}
    </div>
  );
}

function SeasonBubble({ stage }) {
  const { Icon } = stage;
  const etat = etatStage(stage);
  const termine = etat === "termine";
  const sansDate = etat === "date_a_venir";
  return (
    <Link
      to={`/stages/${stage.id}`}
      data-testid={`stage-bubble-${stage.id}`}
      className="group relative overflow-hidden block text-left rounded-[36px] p-8 md:p-10 transition-all duration-500 hover:-translate-y-1"
      style={{
        background: "#fff",
        border: "1px solid var(--border-soft)",
        boxShadow: "0 20px 60px rgba(15,27,45,0.10)",
        opacity: termine ? 0.72 : 1,
      }}
    >
      {/* Blob backgrounds */}
      <div
        className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-40 transition-transform duration-700 group-hover:scale-110"
        style={{ background: stage.accent_soft }}
        aria-hidden="true"
      />
      <div
        className="absolute -bottom-20 -left-10 w-40 h-40 rounded-full opacity-30 transition-transform duration-700 group-hover:scale-110"
        style={{ background: stage.accent_soft }}
        aria-hidden="true"
      />

      <div className="relative z-10">
        {/* Icon + status badge */}
        <div className="flex items-start justify-between gap-3 mb-6">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-md"
            style={{ background: stage.accent, color: "#fff" }}
          >
            <Icon size={26} />
          </div>
          {termine ? (
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest shadow-sm"
              style={{ background: "#E5E7EB", color: "#4B5563" }}
              data-testid={`stage-bubble-closed-${stage.id}`}
            >
              Terminé
            </span>
          ) : sansDate ? (
            <span
              className="inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest"
              style={{ background: stage.accent_soft, color: stage.accent }}
              data-testid={`stage-bubble-comingsoon-${stage.id}`}
            >
              Date à venir
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest shadow-md"
              style={{ background: "var(--primary)", color: "#fff" }}
              data-testid={`stage-bubble-open-${stage.id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Inscriptions ouvertes
            </span>
          )}
        </div>

        <div className="eyebrow" style={{ color: stage.accent }}>
          {stage.season}
        </div>
        <h2 className="font-display text-3xl md:text-4xl mt-2 tracking-tight leading-tight">
          {stage.title}
        </h2>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-sm text-[var(--muted)]">
          <span className="inline-flex items-center gap-2">
            <Calendar size={14} /> {stage.date_label}
          </span>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span
            className="inline-flex items-center px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest"
            style={{ background: stage.accent_soft, color: stage.accent }}
          >
            {stage.theme}
          </span>
          {!sansDate && (
            <span
              className="inline-flex items-center px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest"
              style={{ background: "var(--primary-tint)", color: "var(--primary)" }}
            >
              {stage.age_range}
            </span>
          )}
        </div>

        <div
          className="mt-8 pt-6 flex items-center justify-between"
          style={{ borderTop: "1px dashed var(--border-soft)" }}
        >
          <div>
            {sansDate ? (
              <div className="text-sm text-[var(--muted)] max-w-[180px]" aria-hidden="true">
                Âges, tarifs et inscriptions annoncés plus tard
              </div>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-widest text-[var(--muted)] font-semibold">
                  Tarif dès
                </div>
                <div
                  className="font-display text-3xl"
                  style={{ color: termine ? "var(--muted)" : stage.accent }}
                  aria-hidden="true"
                >
                  {stage.tarif_from}
                </div>
              </>
            )}
            {/* Même information en une phrase complète : les extracteurs de texte des
                robots et des assistants IA lisent une suite de mots, pas une mise en page. */}
            <span className="sr-only">
              {stage.title} — {stage.theme} · {stage.date_label}
              {sansDate ? " · âges, tarifs et inscriptions annoncés plus tard" : ` · ${stage.age_range} · tarif dès ${stage.tarif_from}`}
              {termine ? " · stage terminé" : ""}.
            </span>
          </div>
          <div
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all group-hover:scale-105 group-hover:shadow-md shrink-0"
            style={termine ? { background: "#fff", color: "var(--text)", border: "1px solid var(--border)" } : { background: "var(--primary)", color: "#fff" }}
          >
            {termine ? "Revoir le stage" : "Voir les détails"} <ArrowRight size={16} />
          </div>
        </div>
      </div>
    </Link>
  );
}
